import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo,
  Redo,
  Minus,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  content: string;
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export function RichTextEditor({
  content,
  onChange,
  placeholder = "Write your message...",
  className,
  minHeight = "120px",
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    content,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none min-h-full",
          "text-foreground",
          "[&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1",
          "[&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
          "[&_.is-editor-empty:first-child::before]:text-muted-foreground",
          "[&_.is-editor-empty:first-child::before]:float-left",
          "[&_.is-editor-empty:first-child::before]:h-0",
          "[&_.is-editor-empty:first-child::before]:pointer-events-none"
        ),
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      onChange(html, text);
    },
  });

  // Sync external content changes (e.g., from template selection)
  React.useEffect(() => {
    if (!editor) return;
    
    const currentHtml = editor.getHTML();
    // Only update if content actually changed from external source
    if (content !== currentHtml) {
      // Clear content if empty
      if (content === "" || content === "<p></p>") {
        editor.commands.clearContent();
      } else {
        // Set new content (e.g., from template selection)
        editor.commands.setContent(content, { emitUpdate: false });
      }
    }
  }, [content, editor]);

  if (!editor) return null;

  return (
    <div className={cn("border rounded-md bg-background overflow-hidden", className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-1 border-b bg-muted/30 flex-wrap">
        <Toggle
          size="sm"
          pressed={editor.isActive("bold")}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          className="h-6 w-6 p-0"
          aria-label="Bold"
        >
          <Bold className="h-3 w-3" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("italic")}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          className="h-6 w-6 p-0"
          aria-label="Italic"
        >
          <Italic className="h-3 w-3" />
        </Toggle>
        
        <div className="w-px h-4 bg-border mx-0.5" />
        
        <Toggle
          size="sm"
          pressed={editor.isActive("bulletList")}
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
          className="h-6 w-6 p-0"
          aria-label="Bullet List"
        >
          <List className="h-3 w-3" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("orderedList")}
          onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
          className="h-6 w-6 p-0"
          aria-label="Numbered List"
        >
          <ListOrdered className="h-3 w-3" />
        </Toggle>
        
        <div className="w-px h-4 bg-border mx-0.5" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="h-6 w-6 p-0"
          aria-label="Horizontal Rule"
        >
          <Minus className="h-3 w-3" />
        </Button>
        
        <div className="flex-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="h-6 w-6 p-0"
          aria-label="Undo"
        >
          <Undo className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="h-6 w-6 p-0"
          aria-label="Redo"
        >
          <Redo className="h-3 w-3" />
        </Button>
      </div>
      
      {/* Editor Content */}
      <div className="p-2 overflow-y-auto" style={{ minHeight, maxHeight: "200px" }}>
        <EditorContent editor={editor} className="text-xs" />
      </div>
    </div>
  );
}
